import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography,
  Paper,
  Button,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PeopleIcon from '@mui/icons-material/People';
import AdminUploadForm from './AdminUploadForm';
import UserList from './UserList';
import CreateUserForm from './CreateUserForm';
import ReleaseNotesPage from '../pages/admin/ReleaseNotesPage';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminDashboard: React.FC = () => {
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const userListRef = useRef<{ refresh: () => void }>(null);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleUserCreated = () => {
    // Refresh the user list when a new user is created
    if (userListRef.current) {
      userListRef.current.refresh();
    }
  };

  const handleUploadSuccess = () => {
    // This will be called when an Excel file is uploaded successfully
    // We can add a global event or use a context to notify the main app to refresh options
    console.log('Excel file uploaded successfully - dropdown options should be refreshed');
    
    // Dispatch a custom event that the main app can listen to
    window.dispatchEvent(new CustomEvent('excelUploadSuccess'));
    
    // Show success message
    setRefreshMessage('✅ Calculator options have been refreshed with the new Excel data!');
    setTimeout(() => setRefreshMessage(null), 5000); // Clear after 5 seconds
  };

  const handleManualRefresh = () => {
    window.dispatchEvent(new CustomEvent('excelUploadSuccess'));
    setRefreshMessage('✅ Calculator options have been manually refreshed!');
    setTimeout(() => setRefreshMessage(null), 5000); // Clear after 5 seconds
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-8">
            <img 
              src="/milestone-logo.svg" 
              alt="Milestone" 
              className="h-10 w-auto"
            />
            <div>
              <h1 className="text-2xl font-medium text-[#0099da]">Admin Dashboard</h1>
            </div>
          </div>
          <Link 
            to="/app"
            style={{
              backgroundColor: '#0076A8',
              color: 'white',
              borderRadius: '0.375rem',
              padding: '0.5rem 1rem',
              fontSize: '1rem',
              border: 'none',
              transition: 'background 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            onMouseOver={e => (e.currentTarget.style.backgroundColor = '#005275')}
            onMouseOut={e => (e.currentTarget.style.backgroundColor = '#0076A8')}
            onMouseDown={e => (e.currentTarget.style.backgroundColor = '#002F42')}
            onMouseUp={e => (e.currentTarget.style.backgroundColor = '#005275')}
          >
            <ArrowBackIcon fontSize="small" />
            <span>Back to Calculator</span>
          </Link>
        </div>

        {/* Main Content */}
        <Paper elevation={3} sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
              <Tab 
                icon={<CloudUploadIcon />} 
                label="Excel Upload" 
                id="admin-tab-0"
                aria-controls="admin-tabpanel-0"
              />
              <Tab 
                icon={<PeopleIcon />} 
                label="User Management" 
                id="admin-tab-1"
                aria-controls="admin-tabpanel-1"
              />
              <Tab
                icon={<RocketLaunchIcon />}
                label="Release Notes"
                id="admin-tab-2"
                aria-controls="admin-tabpanel-2"
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <div className="px-6">
              <Typography variant="h6" gutterBottom>
                SKU Data Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Upload new Excel files to update the SKU mapping data used by the calculator.
              </Typography>
              
              {/* Manual refresh button */}
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleManualRefresh}
                  sx={{ mb: 2 }}
                >
                  🔄 Refresh Calculator Options
                </Button>
                <Typography variant="caption" color="text.secondary" display="block">
                  Use this button to manually refresh the calculator dropdown options after uploading a new Excel file.
                </Typography>
              </Box>
              
              {/* Success message */}
              {refreshMessage && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {refreshMessage}
                </Alert>
              )}
              
              {/* Render just the upload form part of AdminUpload */}
              <div className="max-w-2xl">
                <AdminUploadForm onUploadSuccess={handleUploadSuccess} />
              </div>
            </div>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <div className="px-6">
              <Typography variant="h6" gutterBottom>
                User Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage user accounts and permissions. Create new users or modify existing accounts.
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <UserList ref={userListRef} />
                </Box>
                <Box sx={{ width: { xs: '100%', lg: '400px' }, flexShrink: 0 }}>
                  <CreateUserForm onUserCreated={handleUserCreated} />
                </Box>
              </Box>
            </div>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <div className="px-6">
              <ReleaseNotesPage />
            </div>
          </TabPanel>
        </Paper>
      </div>
    </div>
  );
};

export default AdminDashboard; 